import { useCallback, useMemo } from "react";
import {
  laborCostsInStoresDepartmentBasedRead,
  laborCostsInStoresRoleBasedRead,
  ReadLaborCostsInStores,
} from "@/schemas/laborCostsInStores";
import { DataTableFeatures } from "@/types/dataTableSettings";
import { ZodObject, ZodRawShape, ZodReadonly } from "zod";
import { laborCostsInStoresDepartmentBased } from "@/services/laborCostsInStoresDepartmentBased";
import { laborCostsInStoresRoleBased } from "@/services/laborCostsInStoresRoleBased";
import { useLaborCostsInStoresColumnVisibility } from "@/modules/projects/components/invoiceSpecifications/hooks/useLaborCostsInStoresColumnVisibility";
import { ProjectDependsOn } from "@/schemas/commonFields";
import { useCreateDataTableOptions } from "@/components/data-table/hooks/useCreateDataTableOptions";
import { LaborCostsListService } from "@/types/invoiceSpecification";

export interface InvoiceSpecificationLaborCostsConfig {
  name: string;
  schema: ZodReadonly<ZodObject<ZodRawShape>>;
  service: LaborCostsListService;
  translationKey: string;
}

export function useInvoiceSpecificationLaborCostsList(
  invoiceSpecificationId: string,
  projectDependsOn: ProjectDependsOn,
) {
  const config = useMemo((): InvoiceSpecificationLaborCostsConfig => {
    if (projectDependsOn === "department") {
      return {
        name: "laborCostsInStoresDepartmentBased",
        schema: laborCostsInStoresDepartmentBasedRead,
        service: laborCostsInStoresDepartmentBased,
        translationKey: "departmentBased",
      };
    }
    return {
      name: "laborCostsInStoresRoleBased",
      schema: laborCostsInStoresRoleBasedRead,
      service: laborCostsInStoresRoleBased,
      translationKey: "roleBased",
    };
  }, [projectDependsOn]);

  const options = useCreateDataTableOptions<ReadLaborCostsInStores>(
    config.name,
  );

  const getRowProps = useCallback(
    <T>({ original }: { original: T }): Record<string, unknown> => {
      const { include_on_invoice } = original as ReadLaborCostsInStores;
      return {
        className: include_on_invoice
          ? "bg-green-50 hover:bg-green-100"
          : "bg-red-50 hover:bg-red-100",
      };
    },
    [],
  );

  const initialColumnVisibility = useLaborCostsInStoresColumnVisibility({
    config,
    projectDependsOn,
  });

  const tableFeatures = useMemo(
    (): DataTableFeatures => ({
      initialColumnVisibility,
      getRowProps,
    }),
    [initialColumnVisibility, invoiceSpecificationId],
  );

  return { config, options, tableFeatures };
}
